import React, { useContext, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AuthContext, IntlContext } from "context";
import { LogOut, Menu } from "lucide-react";

import { Button } from "components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "components/ui/sheet";
import logoPrimaryHover from "assets/images/logo/logo2025-primary_hover.svg";
import { ROUTES, buildRoute } from "routes";
import { cn } from "lib/utils";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
        "relative hidden rounded-md px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors md:inline-flex",
        isActive
            ? "text-primary after:absolute after:bottom-0 after:left-1/2 after:h-1.5 after:w-1.5 after:-translate-x-1/2 after:rounded-full after:bg-primary"
            : "text-foreground/80 hover:text-foreground",
    );

const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
        "rounded-md py-3 pl-3 pr-3 text-sm font-bold uppercase transition-colors",
        isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent",
    );

const adminNavItems = [
    { to: buildRoute.admin("users"), labelId: "menu.admin.users" },
    { to: buildRoute.admin("bookings"), labelId: "menu.admin.bookings" },
    { to: buildRoute.admin("reportings"), labelId: "menu.admin.reportings" },
    { to: buildRoute.admin("references"), labelId: "menu.admin.references" },
    { to: buildRoute.admin("invoices"), labelId: "menu.admin.invoices" },
] as const;

const TopbarAdmin: React.FC = () => {
    const { account, logout } = useContext(AuthContext);
    const intl = useContext(IntlContext);
    const [sheetOpen, setSheetOpen] = useState(false);

    return (
        <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-6">
                    <Link className="inline-flex items-center gap-2" to={ROUTES.HOME}>
                        <img src={logoPrimaryHover} alt="Logo Twoghether" className="h-8 w-auto" />
                        <span className="sr-only">{intl.formatMessage("menu.home")}</span>
                    </Link>

                    <nav className="hidden items-center gap-1 md:flex">
                        {adminNavItems.map((item) => (
                            <NavLink key={item.to} className={navLinkClass} to={item.to}>
                                {intl.formatMessage(item.labelId)}
                            </NavLink>
                        ))}
                    </nav>
                </div>

                <div className="flex items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="hidden rounded-xl md:inline-flex">
                                {account?.user.last_name} {account?.user.first_name}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={logout}>
                                <LogOut className="mr-2 h-4 w-4" />
                                {intl.formatMessage("authentication.login.action.logout")}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                        <SheetTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon"
                                className="rounded-xl md:hidden"
                                aria-label={intl.formatMessage("menu.open")}
                            >
                                <Menu className="h-4 w-4" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[min(100vw-2rem,20rem)]">
                            <SheetHeader>
                                <SheetTitle className="text-left text-sm font-bold uppercase tracking-wide">
                                    Administration
                                </SheetTitle>
                            </SheetHeader>
                            <nav className="mt-6 flex flex-col gap-1">
                                {adminNavItems.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        className={mobileNavLinkClass}
                                        to={item.to}
                                        onClick={() => setSheetOpen(false)}
                                    >
                                        {intl.formatMessage(item.labelId)}
                                    </NavLink>
                                ))}
                            </nav>
                            <div className="mt-6 border-t pt-4">
                                <Button variant="outline" className="w-full rounded-xl" onClick={logout}>
                                    <LogOut className="mr-2 h-4 w-4" />
                                    {intl.formatMessage("authentication.login.action.logout")}
                                </Button>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default TopbarAdmin;
